/**
 * A best guess at what display a capture came from: "MacBook Air 15″",
 * "Monitor ~27″ 1440p", "Phone · Pixel 7".
 *
 * No browser API gives a monitor's model or physical size, so this is a lookup
 * on what the browser does give — the CSS screen size, the pixel ratio, the
 * device class and OS from the User-Agent, and the model Chromium names on
 * Android. It is always shown as an estimate, and returns null rather than
 * guess when nothing matches.
 *
 * Where the tables come from:
 * - Apple: the "looks like" sizes macOS offers in Displays settings for each
 *   built-in panel (default first). A scaled Mac reports that size at @2x.
 * - iPad: iPadOS Safari presents itself as macOS, so an iPad is only
 *   recognisable by its screen size.
 * - Windows/Linux: common panel resolutions at their usual sizes. Windows
 *   picks 125–150 % scaling on laptops and 100 % on most desktop monitors, so a
 *   ratio above 1 at 1080p–1600p reads as a laptop.
 */

export interface DisplayInput {
  /** `screen.width/height`, CSS px. */
  screenW: number | null
  screenH: number | null
  dpr: number | null
  /** `desktop` | `mobile` | `tablet` | … from the User-Agent. */
  device?: string | null
  os?: string | null
  /** Chromium's `userAgentData` model, Android only. */
  deviceModel?: string | null
}

const key = (w: number, h: number) => `${w}×${h}`

/** Mac built-in panels: every "looks like" size at @2x. */
const MAC_BUILT_IN: Record<string, string> = {
  // MacBook Pro 14″ (3024×1964)
  [key(1512, 982)]: 'MacBook Pro 14″',
  [key(1800, 1169)]: 'MacBook Pro 14″',
  [key(1312, 848)]: 'MacBook Pro 14″ or 16″',
  [key(1147, 745)]: 'MacBook Pro 14″',
  // MacBook Pro 16″ (3456×2234)
  [key(1728, 1117)]: 'MacBook Pro 16″',
  [key(2056, 1329)]: 'MacBook Pro 16″',
  [key(1496, 967)]: 'MacBook Pro 16″',
  // MacBook Air 13″, M2 and later (2560×1664)
  [key(1470, 956)]: 'MacBook Air 13″',
  [key(1710, 1112)]: 'MacBook Air 13″',
  [key(1280, 832)]: 'MacBook Air 13″',
  [key(1024, 665)]: 'MacBook Air 13″',
  // MacBook Air 15″ (2880×1864)
  [key(1440, 932)]: 'MacBook Air 15″',
  [key(1710, 1107)]: 'MacBook Air 15″',
  [key(1280, 830)]: 'MacBook Air 15″',
  // 13″ MacBook Air (M1) / MacBook Pro 13″ (2560×1600)
  [key(1440, 900)]: 'MacBook 13″',
  [key(1680, 1050)]: 'MacBook 13″',
  [key(1280, 800)]: 'MacBook 13″',
  [key(1024, 640)]: 'MacBook 13″',
  // iMac 24″ (4480×2520)
  [key(2240, 1260)]: 'iMac 24″',
  // Pro Display XDR 32″ (6016×3384)
  [key(3008, 1692)]: 'Pro Display XDR 32″',
}

/**
 * Native panel of each built-in Mac above. A scaled Mac reports its looks-like
 * size at @2x, so CSS × ratio is the size macOS renders at, not the panel:
 * 1710×1107 @2x renders 3420×2214 and is scaled down onto 2880×1864.
 */
const MAC_PANEL: Record<string, string> = {
  'MacBook Pro 14″': '3024×1964',
  'MacBook Pro 16″': '3456×2234',
  'MacBook Air 13″': '2560×1664',
  'MacBook Air 15″': '2880×1864',
  'MacBook 13″': '2560×1600',
  'iMac 24″': '4480×2520',
  'Pro Display XDR 32″': '6016×3384',
}

/** External displays a Mac drives at @2x, by "looks like" size. */
const MAC_HIDPI_EXTERNAL: Record<string, string> = {
  [key(2560, 1440)]: '5K display ~27″ (Studio Display, iMac) or a scaled 4K',
  [key(1920, 1080)]: '4K monitor ~24–32″',
  [key(2304, 1296)]: '4K monitor ~27–32″, scaled',
  [key(3200, 1800)]: '6K display ~32″',
}

/** iPad screens in CSS px, either orientation. */
const IPAD: Record<string, string> = {
  [key(1366, 1024)]: 'iPad Pro 12.9″ / 13″',
  [key(1376, 1032)]: 'iPad Pro 13″',
  [key(1210, 834)]: 'iPad Pro 11″',
  [key(1194, 834)]: 'iPad Pro 11″ / iPad Air 11″',
  [key(1180, 820)]: 'iPad Air / iPad 10.9″',
  [key(1133, 744)]: 'iPad mini',
  [key(1080, 810)]: 'iPad 10.2″',
}

/** iPhones in CSS px, portrait. */
const IPHONE: Record<string, string> = {
  [key(440, 956)]: 'iPhone Pro Max (16/17)',
  [key(430, 932)]: 'iPhone Plus / Pro Max (14–16)',
  [key(402, 874)]: 'iPhone Pro (16/17)',
  [key(393, 852)]: 'iPhone 14 Pro–16',
  [key(390, 844)]: 'iPhone 12–14',
  [key(428, 926)]: 'iPhone 12/13 Pro Max',
  [key(414, 896)]: 'iPhone XR / 11',
  [key(375, 812)]: 'iPhone X–13 mini',
  [key(375, 667)]: 'iPhone SE / 8',
}

/** Panels at their physical resolution, as seen at 100 % scaling. */
const MONITOR: Record<string, string> = {
  [key(1920, 1080)]: 'Monitor ~24″ 1080p',
  [key(1920, 1200)]: 'Monitor ~24″ 1920×1200',
  [key(2560, 1440)]: 'Monitor ~27″ 1440p',
  [key(2560, 1080)]: 'Ultrawide ~29″',
  [key(3440, 1440)]: 'Ultrawide ~34″',
  [key(3840, 1600)]: 'Ultrawide ~38″',
  [key(5120, 1440)]: 'Super-ultrawide ~49″',
  [key(3840, 2160)]: '4K monitor ~27–32″',
  [key(1680, 1050)]: 'Monitor ~22″',
  [key(1600, 900)]: 'Monitor ~20″',
  [key(1440, 900)]: 'Monitor ~19″',
  [key(1280, 1024)]: 'Monitor ~17–19″ 5:4',
  [key(1366, 768)]: 'Laptop ~14–15.6″ 1366×768',
}

/** Physical panels that, scaled above 100 %, are almost always laptops. */
const LAPTOP_PANEL: Record<string, string> = {
  [key(1920, 1080)]: 'Laptop ~14–15.6″ 1080p',
  [key(1920, 1200)]: 'Laptop ~14–16″ 1920×1200',
  [key(2560, 1600)]: 'Laptop ~14–16″ 2560×1600',
  [key(2880, 1800)]: 'Laptop ~14–16″ 2880×1800',
  [key(2560, 1440)]: 'Laptop ~14–16″ 1440p',
  [key(2880, 1620)]: 'Laptop ~14–16″ 3K',
  [key(3840, 2400)]: 'Laptop ~16″ 4K',
  [key(3840, 2160)]: 'Laptop ~15.6″ 4K',
  [key(2256, 1504)]: 'Laptop ~13.5″ 3:2',
  [key(2736, 1824)]: 'Laptop ~13″ 3:2',
  [key(2880, 1920)]: 'Laptop ~13–14″ 3:2',
}

const APPLE_DESKTOP = new Set(['macOS', 'Mac OS', 'Mac OS X'])

const landscape = (w: number, h: number) => (w >= h ? key(w, h) : key(h, w))
const portrait = (w: number, h: number) => (w <= h ? key(w, h) : key(h, w))

export function estimateDisplay(s: DisplayInput): string | null {
  const model = s.deviceModel?.trim() || null
  const { screenW: w, screenH: h, dpr } = s
  const known = w !== null && h !== null

  if (s.device === 'mobile' || s.device === 'tablet') {
    const kind = s.device === 'mobile' ? 'Phone' : 'Tablet'
    if (model) return `${kind} · ${model}`
    if (known && s.os === 'iOS') {
      const iphone = IPHONE[portrait(w, h)]
      if (iphone && s.device === 'mobile') return iphone
    }
    if (known && s.device === 'tablet' && (s.os === 'iPadOS' || s.os === 'iOS' || s.os === 'macOS')) {
      return IPAD[landscape(w, h)] ?? kind
    }
    return kind
  }

  // Only a desktop is judged by its screen; an unknown device class gets no guess.
  if (s.device !== 'desktop' || !known || dpr === null) return model

  if (s.os && APPLE_DESKTOP.has(s.os)) {
    // iPadOS Safari says it is a Mac; only its screen gives it away.
    const ipad = IPAD[landscape(w, h)]
    if (ipad) return `${ipad} (or a Mac at that size)`
    if (dpr === 2) return MAC_BUILT_IN[key(w, h)] ?? MAC_HIDPI_EXTERNAL[key(w, h)] ?? 'Mac, Retina display'
    if (dpr === 1) return MONITOR[key(w, h)] ?? 'External monitor'
    return null
  }

  // Windows, Linux, ChromeOS: judge by the physical panel.
  const pw = Math.round(w * dpr)
  const ph = Math.round(h * dpr)
  const panel = key(pw, ph)
  // Page zoom moves the ratio off 1 but keeps the physical size; only real
  // scaling (125 % and up) says laptop.
  if (dpr < 1.25) return MONITOR[panel] ?? null
  if (dpr > 1 && dpr <= 1.5 && panel === key(3840, 2160)) return '4K monitor ~27–32″'
  return LAPTOP_PANEL[panel] ?? (dpr >= 1.25 ? 'Laptop, high-DPI' : null)
}

/** Whether CSS × ratio can be a scaled render size rather than the panel (macOS HiDPI). */
export function isScaledRender(s: DisplayInput): boolean {
  return s.device === 'desktop' && !!s.os && APPLE_DESKTOP.has(s.os) && s.dpr !== null && s.dpr > 1
}

/**
 * The panel's native resolution, when the capture pins down which built-in
 * Mac it is; null otherwise (including every external display).
 */
export function nativePanel(s: DisplayInput): string | null {
  if (!isScaledRender(s) || s.dpr !== 2 || s.screenW === null || s.screenH === null) return null
  const mac = MAC_BUILT_IN[key(s.screenW, s.screenH)]
  return mac ? MAC_PANEL[mac] ?? null : null
}
